import { useCallback, useMemo, useState } from 'react';
import { useStudio } from '../../store/studioStore';
import { loadUploadedFont } from '../../core/fonts/fontLoader';
import { buildTextSchema } from '../../core/text/textParams';
import { AnchorGrid, Row } from '../controls/Basics';
import { FontPicker } from '../controls/FontPicker';
import { ParamList } from '../params/ParamList';

/**
 * The text panel.
 *
 * Everything that can be a slider *is* a slider generated from the text schema, so the
 * text parameters get lock, randomise and exploration ranges for free and the batch
 * engine can vary them without special cases. Only three controls stay bespoke,
 * because no generic control would serve them: the font picker (live previews and
 * drag-and-drop upload), the anchor grid (a 3×3 pad) and the font's own axis list,
 * which is generated into the schema per font.
 */
export function TextPanel(): JSX.Element {
  const doc = useStudio((s) => s.doc);
  const font = useStudio((s) => s.font);
  const fontLoading = useStudio((s) => s.fontLoading);
  const fontError = useStudio((s) => s.fontError);
  const setTextValue = useStudio((s) => s.setTextValue);
  const setTextBulk = useStudio((s) => s.setTextBulk);
  const toggleTextLock = useStudio((s) => s.toggleTextLock);
  const randomizeTextParam = useStudio((s) => s.randomizeTextParam);
  const setFontId = useStudio((s) => s.setFontId);
  const setAnchor = useStudio((s) => s.setAnchor);
  const setFont = useStudio((s) => s.setFont);
  const setFontError = useStudio((s) => s.setFontError);
  const search = useStudio((s) => s.paramSearch);

  const [uploadVersion, setUploadVersion] = useState(0);
  const schema = useMemo(() => buildTextSchema(font), [font]);

  const handleUpload = useCallback(
    async (file: File) => {
      try {
        setFontError(null);
        const loaded = await loadUploadedFont(file);
        setFont(loaded);
        setFontId(loaded.id);
        setUploadVersion((v) => v + 1);
      } catch (err) {
        setFontError((err as Error).message);
      }
    },
    [setFont, setFontError, setFontId],
  );

  const fontBlock = (
    <div className="pb-3">
      <FontPicker
        value={doc.fontId}
        onChange={setFontId}
        onUpload={(f) => void handleUpload(f)}
        uploadedVersion={uploadVersion}
      />
      {fontLoading && <p className="mt-1 text-[10px] text-ink-500">Font wird geladen…</p>}
      {fontError && <p className="mt-1 text-[10px] text-red-400">{fontError}</p>}
      {font && !fontLoading && (
        <p className="mt-1 font-mono text-[9px] text-ink-600">
          {font.font.numGlyphs} Glyphen · {font.unitsPerEm} upm ·{' '}
          {font.font.outlinesFormat === 'cff' ? 'CFF' : 'TrueType'}
          {font.axes.length > 0 && ` · ${font.axes.length} variable Achsen`}
          {!font.hasWeightAxis && ' · festes Gewicht'}
        </p>
      )}
    </div>
  );

  return (
    <div className="px-3">
      <ParamList
        schema={schema}
        values={doc.textValues}
        bulk={doc.textBulk}
        onChange={setTextValue}
        onBulkChange={setTextBulk}
        onToggleLock={toggleTextLock}
        onRandomize={randomizeTextParam}
        search={search}
        header={fontBlock}
        groupExtras={{
          'Position & Verzerrung': (
            <Row label="Anker" tooltip="Bezugspunkt des Textblocks im Canvas">
              <AnchorGrid x={doc.anchorX} y={doc.anchorY} onChange={setAnchor} />
            </Row>
          ),
        }}
      />
    </div>
  );
}
